import React from "react";

import Card from "react-bootstrap/Card";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";

import classNames from "classnames";
import { Link } from "react-router-dom";

import { ParticipantStatus, ParticipantTypes, UserStatus } from "@api/graphql";
import { DrawerUploadIcon, PlusIcon, RightArrowIcon } from "@assets/icons";
import { IconButton } from "@components/IconButton";
import { IconLink } from "@components/IconLink";
import { PendingNotification } from "@components/PendingNotification";
import { TaskNotification } from "@components/TaskNotification";
import { UserDisplay } from "@components/UserDisplay";
import { ProtectedComponent, useIsAuthorisedUser } from "@components/permission_control/ProtectedComponent";
import { DownloadexcelLink } from "@components/resale_submission/csv/DownloadexcelLink";
import { useAuthenticationContext } from "@context/authentication";
import { Role } from "@roles";

import { ROUTES } from "../../routes";

import styles from "./ReportNewResale.module.scss";

export const ReportNewResale = () => {
  const { auth } = useAuthenticationContext();
  const isActiveUser = useIsAuthorisedUser({
    requiredGroups: [Role.ResaleReporter, Role.MemberServices, Role.VisualArtistAdmin, Role.Data],
    requiredUserStatus: UserStatus.ACTIVE,
    requiredParticipantStatus: ParticipantStatus.ACTIVE,
  });

  const isInternalUserOrResaleReporter = useIsAuthorisedUser({
    requiredGroups: [Role.VisualArtistAdmin, Role.Data, Role.ResaleReporter],
  });
  const isCMO = useIsAuthorisedUser({ requiredParticipantTypes: [ParticipantTypes.CMO] });

  const outstandingInvoices: string[] = []; //TODO:NRRS-550 get the type from backend
  return (
    <Container className="p-4" fluid>
      <Row className="pb-4">
        <Col>
          <UserDisplay />
        </Col>
      </Row>
      <Row className="pb-4">
        <Col>
          <h2>Report new resale</h2>
        </Col>
      </Row>
      <ProtectedComponent auth={auth} requiredGroups={[Role.ResaleReporter]}>
        {outstandingInvoices.length > 0 &&
          outstandingInvoices.map((invoice) => {
            const messageList = ["created on " + invoice, "Invoice no " + invoice]; //TODO:NRRS-550 created date, invoice number and amount
            return (
              <Row key={invoice}>
                <TaskNotification
                  title={
                    <span>
                      You have 1 outstanding invoice of <b> {invoice} </b>
                    </span>
                  }
                  messageList={messageList}
                  to={ROUTES.Dashboard.PendingPayments.path}
                  buttoneTxt={"Check now"}
                ></TaskNotification>
              </Row>
            );
          })}
      </ProtectedComponent>
      <ProtectedComponent auth={auth} requiredUserStatus={UserStatus.PENDING_APPROVAL}>
        <Row key={"invoice"}>
          <PendingNotification title={<span>We are still reviewing your account.</span>}></PendingNotification>
        </Row>
      </ProtectedComponent>

      <Row className={classNames(styles["card-row"], "pb-2")}>
        <Col className="d-flex flex-row flex-wrap align-items-stretch">
          <Card
            bg="info"
            className={classNames(styles["card"], {
              [styles.disabled]: !isActiveUser && !isInternalUserOrResaleReporter,
            })}
          >
            <Card.Body className="d-flex flex-column gap-3">
              <Link className={styles["link"]} to={ROUTES.ResaleCsvSubmission.path}>
                <IconButton
                  variant="quaternary"
                  src={DrawerUploadIcon}
                  disabled={!isActiveUser && !isInternalUserOrResaleReporter}
                ></IconButton>
              </Link>
              <Card.Title>Upload an excel sheet</Card.Title>
              <Card.Text>
                For more than 10 resales, we recommend downloading the excel sheet to report the resales.
              </Card.Text>
              <DownloadexcelLink isCMO={isCMO} />
            </Card.Body>
          </Card>

          {!isCMO && (
            <>
              <div className={classNames(styles["or"], "align-self-center")}>OR</div>

              <Card
                bg="info"
                className={classNames(styles["card"], {
                  [styles.disabled]: !isActiveUser && !isInternalUserOrResaleReporter,
                })}
              >
                <Card.Body className="d-flex flex-column gap-3">
                  <Link className={styles["link"]} to={ROUTES.ResaleSubmission.path}>
                    <IconButton
                      variant="quaternary"
                      src={PlusIcon}
                      disabled={!isActiveUser && !isInternalUserOrResaleReporter}
                    ></IconButton>
                  </Link>
                  <Card.Title>Use online reporting form</Card.Title>
                  <Card.Text>For less than 10 resales, we recommend using our simple web form</Card.Text>
                </Card.Body>
              </Card>
            </>
          )}
        </Col>
      </Row>
      {!isCMO && (
        <Row className="pb-5">
          <IconLink
            to={ROUTES.HowToUseTemplate.path}
            src={RightArrowIcon}
            text="How to use the template"
            iconPosition="right"
            target="_blank"
          />
        </Row>
      )}
      <Row className="pb-5">
        <Col>
          <h2>How it works</h2>
        </Col>
      </Row>
      <Row className={styles["explanations"]}>
        <Col xs="auto">
          <h3>01</h3>
        </Col>
        <Col>
          <Row>
            <Col>
              <h5>We review your reported resales</h5>
            </Col>
          </Row>
          <Row>
            <Col>
              <span className="b4">
                You report eligible work(s) that were resold using the tools above. We review and verify your resale.
                Once a resale has been marked eligible, we notify you and the corresponding artist(s) or their
                representatives.
              </span>
            </Col>
          </Row>
        </Col>
        <Col xs="auto">
          <h3>02</h3>
        </Col>
        <Col>
          <Row>
            <Col>
              <h5>You will be invoiced for royalties to be paid</h5>
            </Col>
          </Row>
          <Row>
            <Col>
              <span className="b4">
                The eligible resale(s) will be placed on hold for the mandatory 21 day period. If the artists hasn’t
                opted out of the royalties, you will be sent an invoice to make the royalty payment.
              </span>
            </Col>
          </Row>
        </Col>
        <Col xs="auto">
          <h3>03</h3>
        </Col>
        <Col>
          <Row>
            <Col>
              <h5>Pay royalties for the eligible resales</h5>
            </Col>
          </Row>
          <Row>
            <Col>
              <span className="b4">
                The invoice will advise the total amount to be paid. This will then be distributed to the artist(s) or
                their representative, less our admin fee and GST.
              </span>
            </Col>
          </Row>
        </Col>
      </Row>
    </Container>
  );
};
